import React, { useId } from "react";
import { Button, Spinner } from "react-bootstrap";
import "./security.css";
import "./methodOrder.css";
import useMethodOrder from "./useMethodOrder";

// One reorder button. The visible text stays "Move up"; the hidden method name makes the accessible name
// "Move Email up", so the label still contains what is on screen (WCAG 2.5.3).
const MoveButton = ({ label, direction, disabled, onClick }) => (
  <Button variant="outline-secondary" size="sm" className="mfa-btn-quiet" type="button" disabled={disabled}
    onClick={onClick}>
    Move <span className="visually-hidden">{label} </span>{direction}
  </Button>
);

const MethodRows = ({ order }) => (
  <ol className="mfa-method-list">
    {order.rows.map((row, index) => (
      <li key={row.method} className="mfa-method-row">
        <div className="mfa-method-text">
          <strong>{row.label}</strong>
          <span>{row.detail}</span>
        </div>
        <div className="mfa-method-moves">
          <MoveButton label={row.label} direction="up" disabled={index === 0 || order.saving}
            onClick={() => order.move(index, -1)} />
          <MoveButton label={row.label} direction="down" disabled={index === order.rows.length - 1 || order.saving}
            onClick={() => order.move(index, 1)} />
        </div>
      </li>
    ))}
  </ol>
);

const SectionBody = ({ order, onSignInAgain }) => {
  if (order.signInRequired) {
    return (
      <div className="alert alert-danger" role="alert">
        Please sign in again. <button type="button" onClick={onSignInAgain}>Sign in again</button>
      </div>
    );
  }
  if (order.loading) return <p><Spinner animation="border" size="sm" /> Loading your sign-in methods...</p>;
  if (order.error) {
    return <div><div className="alert alert-danger" role="alert">{order.error}</div><Button onClick={order.retry}>Retry</Button></div>;
  }
  return (
    <>
      {!order.chosen && <p className="text-muted">Using the default order.</p>}
      <MethodRows order={order} />
      {order.saveError && <div className="alert alert-danger" role="alert">{order.saveError}</div>}
      <div className="mfa-actions mfa-method-save">
        <Button type="button" className="text-white fw-bold" disabled={!order.changed || order.saving}
          onClick={order.save}>
          Save order
        </Button>
        {order.saved && <span role="status" className="mfa-method-saved">Saved.</span>}
      </div>
    </>
  );
};

// docs/specs/login-setup.md §4: My Profile -> Security, "Sign-in method order". No step-up (Sanil): every method
// the user has stays selectable at sign-in, and this only changes which one is tried first.
const MethodOrderSection = ({ onSignInAgain }) => {
  const headingId = useId();
  const order = useMethodOrder();
  return (
    <section className="mfa-card" aria-labelledby={headingId}>
      <div className="mfa-card-head">
        <h2 id={headingId}>Sign-in method order</h2>
      </div>
      <p>When you sign in, we use the first method on this list that you have set up.</p>
      <SectionBody order={order} onSignInAgain={onSignInAgain} />
    </section>
  );
};

export default MethodOrderSection;
